import React from 'react';
import { CreditCard, DollarSign, Shield, TrendingUp } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

const CreditCardPage: React.FC = () => {
  useSEO({
    title: 'Cartão de Crédito em 2026: Milhas, Cashback e Como Evitar Juros | O Teu Dinheiro',
    description: 'Aprenda a usar o cartão de crédito a seu favor: acumule milhas e cashback, evite os juros do rotativo e compare os melhores cartões de 2026.',
    keywords: 'cartão de crédito 2026, melhores cartões de crédito, milhas, cashback, juros rotativo cartão, cartão sem anuidade',
    url: 'https://www.oteudinheiro.com.br/cartao-credito'
  });
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-600 to-red-700 text-white py-20">
        <div className="container-custom text-center">
          <CreditCard className="w-16 h-16 mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Cartão de Crédito
          </h1>
          <p className="text-xl md:text-2xl text-red-100 max-w-3xl mx-auto mb-8">
            Como usar o cartão de crédito de forma inteligente e sem dívidas
          </p>
          <a
            href="/blog/melhores-cartoes-2026-alta-renda"
            className="inline-flex items-center px-6 py-3 bg-white text-red-700 font-bold rounded-xl shadow-md hover:bg-gray-100 transition-colors"
          >
            🏆 Ver Ranking: Melhores Cartões de Crédito 2026 (Black, Milhas e Cashback) →
          </a>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left Column */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                O que é um cartão de crédito?
              </h2>

              <div className="prose prose-lg text-gray-600 mb-8">
                <p className="mb-4">
                  O cartão de crédito é uma ferramenta financeira que permite fazer compras
                  a crédito, ou seja, você compra hoje e paga depois.
                </p>
                <p className="mb-4">
                  É como um empréstimo de curto prazo: o banco paga suas compras e você
                  devolve o dinheiro na data de vencimento da fatura.
                </p>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Como funciona
              </h3>

              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3">
                  <div className="bg-red-100 p-2 rounded-lg">
                    <span className="text-lg font-bold text-red-600">1</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Compra</h4>
                    <p className="text-gray-600 text-sm">Use o cartão para fazer compras</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="bg-red-100 p-2 rounded-lg">
                    <span className="text-lg font-bold text-red-600">2</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Fatura</h4>
                    <p className="text-gray-600 text-sm">Receba a fatura com todas as compras</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="bg-red-100 p-2 rounded-lg">
                    <span className="text-lg font-bold text-red-600">3</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Pagamento</h4>
                    <p className="text-gray-600 text-sm">Pague a fatura até a data de vencimento</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div>
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Tipos de cartão
                </h3>

                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">Cartão básico</h4>
                    <p className="text-gray-600 text-sm">Sem anuidade, limite baixo, ideal para iniciantes</p>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">Cartão com benefícios</h4>
                    <p className="text-gray-600 text-sm">Pontos, milhas, cashback, mas pode ter anuidade</p>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">Cartão premium</h4>
                    <p className="text-gray-600 text-sm">Alto limite, muitos benefícios, anuidade alta</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Vantagens do cartão de crédito
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="flex items-start space-x-3">
              <div className="bg-green-100 p-2 rounded-lg">
                <Shield className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Segurança</h3>
                <p className="text-gray-600">
                  Não precisa carregar dinheiro em espécie. Se for roubado,
                  você pode bloquear e não perde dinheiro.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Benefícios</h3>
                <p className="text-gray-600">
                  Pontos, milhas, cashback e outros benefícios que podem
                  gerar economia ou vantagens extras.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="bg-purple-100 p-2 rounded-lg">
                <DollarSign className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Controle</h3>
                <p className="text-gray-600">
                  Fatura mensal com todas as despesas organizadas,
                  facilitando o controle financeiro.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="bg-orange-100 p-2 rounded-lg">
                <CreditCard className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Emergências</h3>
                <p className="text-gray-600">
                  Útil para situações emergenciais quando você não
                  tem dinheiro disponível no momento.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Risks Section */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Riscos e cuidados
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="warning-box">
              <h3 className="text-xl font-semibold text-accent-900 mb-3">
                Juros altos
              </h3>
              <p className="text-accent-700">
                Se não pagar a fatura completa, os juros podem chegar a 400% ao ano.
                É uma das formas mais caras de financiamento.
              </p>
            </div>

            <div className="warning-box">
              <h3 className="text-xl font-semibold text-accent-900 mb-3">
                Dívidas acumuladas
              </h3>
              <p className="text-accent-700">
                Fácil de perder o controle e acumular dívidas. Uma compra pequena
                pode se tornar uma bola de neve.
              </p>
            </div>

            <div className="warning-box">
              <h3 className="text-xl font-semibold text-accent-900 mb-3">
                Anuidade e taxas
              </h3>
              <p className="text-accent-700">
                Muitos cartões cobram anuidade e outras taxas que podem
                comprometer seu orçamento.
              </p>
            </div>

            <div className="warning-box">
              <h3 className="text-xl font-semibold text-accent-900 mb-3">
                Limite de crédito
              </h3>
              <p className="text-accent-700">
                O limite pode ser reduzido ou o cartão cancelado se você
                não usar adequadamente.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Tips Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Como usar de forma inteligente
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-red-600">1</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Pague sempre a fatura completa
              </h3>
              <p className="text-gray-600">
                Nunca pague apenas o valor mínimo. Isso evita juros e
                mantém seu cartão saudável.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-red-600">2</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Use apenas para despesas planejadas
              </h3>
              <p className="text-gray-600">
                Não use o cartão para compras por impulso. Tenha um
                planejamento financeiro claro.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-red-600">3</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Monitore sua fatura
              </h3>
              <p className="text-gray-600">
                Acompanhe suas compras pelo app do banco e verifique
                se não há cobranças indevidas.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-red-600 text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold mb-6">
            Use o cartão de crédito com responsabilidade!
          </h2>
          <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
            É uma ferramenta útil, mas pode se tornar um problema se não for usada corretamente.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/dividas"
              className="btn bg-white text-red-600 hover:bg-gray-100"
            >
              Gerenciar dívidas
            </a>
            <a
              href="/calculadoras"
              className="btn bg-transparent text-white border-2 border-white hover:bg-white hover:text-red-600"
            >
              Calcular juros
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CreditCardPage;
